import { Chip, Tooltip } from "@mui/material";
import type { JSX } from "react";

type EmailLike = { email: string | null; email_status: string };

/** One clear answer to "is this email real?" — no more vague likely/guessed.
 * Verified ✓ = the paid verifier confirmed deliverable; Invalid = mailbox
 * doesn't exist; Catch-all = domain accepts everything (can't confirm); Guess =
 * a pattern guess not yet verified. */
export function emailStatusMeta(c: EmailLike): {
  label: string;
  color: "success" | "error" | "warning" | "default";
  tip: string;
} {
  if (!c.email) {
    return { label: "No email", color: "default", tip: "No email found for this contact yet." };
  }
  switch (c.email_status) {
    case "valid":
      return { label: "Verified ✓", color: "success", tip: "Confirmed deliverable by the email verifier — this is a real mailbox." };
    case "invalid":
      return { label: "Invalid", color: "error", tip: "The verifier says this mailbox does not exist. Don't use it." };
    case "catchall":
      return { label: "Catch-all", color: "warning", tip: "The domain accepts every address, so the verifier can't confirm this specific mailbox." };
    case "unknown":
      return { label: "Unconfirmed", color: "warning", tip: "The verifier couldn't reach a verdict for this address." };
    default:
      return { label: "Unverified guess", color: "default", tip: "A pattern-based guess (name + domain) — not yet verified." };
  }
}

export function EmailStatusBadge({ contact }: { contact: EmailLike }): JSX.Element {
  const { label, color, tip } = emailStatusMeta(contact);
  return (
    <Tooltip title={tip}>
      <Chip
        label={label}
        size="small"
        color={color}
        variant={color === "success" ? "filled" : "outlined"}
        sx={{ borderRadius: 1, fontWeight: 500 }}
      />
    </Tooltip>
  );
}
